
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Getter, namespace } from "vuex-class";
import vSelect3 from "vselect3";
import axios, { CancelTokenSource } from "axios";
import { debounce, differenceBy, isEqual, unionWith } from "lodash";
import api from "@/services/api.service";
import { UserInfo, UserPermissions, UserTypeFilterEnum, VueSelectDropdownProperties } from "@/store/types";
import { AddInvolvedPartyRequest, InvolvedParty, RemoveInvolvedPartyRequest } from "@/store/site-monitor/types";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { truncateString } from "@/filters";
const SiteMonitor = namespace("siteMonitor");

@Component({
    components: {
		VuePerfectScrollbar: VuePerfectScrollbar,
        "v-select-3": vSelect3
    },
	filters: { truncateString }
})
export default class InvolvedParties extends Vue {
    @Prop(Number)
    eventId?: number;

	$refs!: {
		involvedPartiesModal: any;
	};

	private readonly debouncePeriod: number = process.env.NODE_ENV === "test" ? 1 : 500;
	private readonly maxUserLimit: number = 100;
	private readonly maxWidth: string = '50vw';

	private readonly noContactsFound: string = 'No contacts were found';
	private readonly noUsersFound: string = 'No users or contacts were found';

    private selectedParties: UserInfo[] = [];
	private involvedPartySearchToken: CancelTokenSource;
	private users: UserInfo[] = [];
	private involvementType: string | null = null;
	private involvedParties: InvolvedParty[] = [];
	private includeUsers: boolean = false;
	private currentSearchTerm: string = "";

	@Getter("getPermissions") permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

    @SiteMonitor.Getter("getInvolvedPartiesShown") involvedPartiesShown!: boolean;

    @SiteMonitor.Mutation setInvolvedPartiesShown: (shown: boolean) => void;

    @Watch("involvedPartiesShown")
    private async onInvolvedPartiesShownChanged(value: boolean): Promise<void> {
        if (value) {
			await this.updateInvolvedParties();
			this.clearInputs();
            this.$refs.involvedPartiesModal.show();
        } else {
            this.$refs.involvedPartiesModal.hide();
        }
    }

	private get canEditInvolvedParties(): boolean {
		return !!this.permissions.canEditInvolvedParties;
	}

	private get isValid(): boolean {
		if (this.selectedParties.length == 0)
		{
			return false;
		}

		if (!this.involvementType || this.involvementType.trim() === "")
		{
			return false;
		}

		return true;
	}

    public created(): void {
        this.setInvolvedPartiesShown(false);
		if (!this.excludeUsersEnabled) {
			this.includeUsers = true;
		}
    }

	public destroy(): void {
		this.currentSearchTerm = "";
	}

	private get excludeUsersEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "InvolvedParties","ExcludeUsers"]);
	}

    public closeInvolvedParties(): void {
        this.setInvolvedPartiesShown(false);
    }

	private async handleIncludeUsersChange(): Promise<void> {
		this.users = [];
		await this.performInvolvedPartySearch(this.currentSearchTerm);
	}

	private async handleInvolvedPartySearchInput(searchTerm: string): Promise<void> {
		this.currentSearchTerm = searchTerm;
		await this.performInvolvedPartySearch(searchTerm);
	}

	private performInvolvedPartySearch: (searchTeam: string) => Promise<void> = debounce(
		async function (searchTerm: string) {
			if (this.involvedPartySearchToken) {
				this.involvedPartySearchToken.cancel("Cancel Search");
			}

			this.involvedPartySearchToken = axios.CancelToken.source();

			try {
				let usersInfo = await api.getUsersInfoBySearchTerm(
					searchTerm,
					this.includeUsers ? UserTypeFilterEnum.UsersAndContacts : UserTypeFilterEnum.ContactsOnly,
					this.maxUserLimit);
				this.users = unionWith(usersInfo, this.selectedParties, isEqual);
				let existingInvolvedParties = this.involvedParties.filter(ip => ip.added);
				this.users = differenceBy(this.users, existingInvolvedParties, 'userId');
			} catch (e) {
				return; //Return when past requests are cancelled
			}
		},
		this.debouncePeriod,
		{ leading: false, trailing: true }
	);

	private async addInvolvedParty(): Promise<void> {
		let involvedPartiesToAdd: AddInvolvedPartyRequest[] = [];

		this.selectedParties.forEach(involvedParty => {
			involvedPartiesToAdd.push({
				eventId: this.eventId,
				userId: involvedParty.userId,
				involvementType: this.involvementType
				} as AddInvolvedPartyRequest)
		});

		for (var i = 0; i < involvedPartiesToAdd.length; i++) {
			await api.addInvolvedParty(involvedPartiesToAdd[i]);
		}

		this.selectedParties = [];
		this.users = [];
		this.involvementType = null;

		await this.updateInvolvedParties();
	}

	private async updateInvolvedParties(): Promise<void> {
		this.involvedParties = await api.getInvolvedPartiesForEvent(this.eventId);
	}

	private clearInputs(): void {
		this.selectedParties = [];
		this.involvementType = null;
	}

	private async removeInvolvedParty(involvedParty: InvolvedParty): Promise<void> {
		await api.removeInvolvedParty({ eventId: this.eventId, userId: involvedParty.userId } as RemoveInvolvedPartyRequest);
		await this.updateInvolvedParties();
	}

	private getUserTitle(user: UserInfo): string {
		var userDisplay = user.fullName;

		if (user.notes != "") {
			userDisplay += ` [${user.notes}]`;
		}

		if (user.email && user.email != "") {
			userDisplay += ` (${user.email})`;
		}

		return userDisplay;
	}

	private calculateDropdownPositionAndSize(dropdownList: HTMLUListElement, component: Vue, { width, top, left }: VueSelectDropdownProperties): void {
        dropdownList.style.top = top;
        dropdownList.style.left = left;
        dropdownList.style.width = 'auto';
		dropdownList.style.minWidth = width;
		dropdownList.style.maxWidth = this.maxWidth;
    }
}
